import { Menu } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import UilEllipsisV from '@iconscout/react-unicons/icons/uil-ellipsis-v';
import propTypes from 'prop-types';
import { getAdminMenuItems } from './menu/AdminMenuItems';
import { getBorrowerMenuItems } from './menu/BorrowerMenuItems';
import { getInvestorMenuItems } from './menu/InvestorMenuItems';
import { getFacilitatorMenuItems } from './menu/FacilitatorMenuItems';

function MenuItems({ toggleCollapsed }) {
  const location = useLocation();

  const { topMenu, userType } = useSelector((state) => {
    return {
      topMenu: state.ChangeLayoutMode.topMenu,
      userType: state.auth.userType,
    };
  });

  // Get the base path based on userType and current location
  const pathName = location.pathname;
  const basePath = (() => {
    if (userType === 'admin') return '/admin';
    // If userType is borrower, their base path is '/manager' for menu generation
    if (userType === 'borrower') return '/manager';
    if (userType === 'investor') return '/investor';
    if (userType === 'facilitator') return '/facilitator';
    // Fallback based on pathname (useful for initial load or if userType isn't immediately available)
    if (pathName.startsWith('/admin')) return '/admin';
    if (pathName.startsWith('/manager') && userType === 'borrower') return '/manager'; // Ensure manager path is recognized for borrower
    if (pathName.startsWith('/investor')) return '/investor';
    if (pathName.startsWith('/facilitator')) return '/facilitator';
    return '/admin'; // default fallback if nothing matches
  })();

  // Safely handle path splitting
  // Use the actual pathName for splitting, but prefix with the derived basePath for consistency
  const actualPathForSplitting = pathName.startsWith(basePath) ? pathName.replace(basePath, '') : pathName;
  const mainPathSplit = actualPathForSplitting.split('/').filter(Boolean);

  const [openKeys, setOpenKeys] = React.useState(
    !topMenu ? [`${mainPathSplit.length > 1 ? mainPathSplit[0] : 'dashboard'}`] : [],
  );

  const onOpenChange = (keys) => {
    setOpenKeys(keys[keys.length - 1] !== 'recharts' ? [keys.length && keys[keys.length - 1]] : keys);
  };

  const onClick = (item) => {
    if (item.keyPath.length === 1) setOpenKeys([]);
  };

  const getMenuItems = () => {
    switch (userType) {
      case 'admin':
        return getAdminMenuItems(basePath, toggleCollapsed, topMenu);
      case 'borrower':
        // Pass '/manager' as the base path for borrower menu items
        return getBorrowerMenuItems('/manager', toggleCollapsed, topMenu);
      case 'investor':
        return getInvestorMenuItems(basePath, toggleCollapsed, topMenu);
      case 'facilitator':
        return getFacilitatorMenuItems(basePath, toggleCollapsed, topMenu);
      default:
        // Use the derived basePath for default (e.g., if userType is not yet loaded)
        return getAdminMenuItems(basePath, toggleCollapsed, topMenu);
    }
  };

  return (
    <Menu
      onOpenChange={onOpenChange}
      onClick={onClick}
      mode={!topMenu || window.innerWidth <= 991 ? 'inline' : 'horizontal'}
      defaultSelectedKeys={[
        `${mainPathSplit.length === 1 ? 'home' : mainPathSplit.length === 2 ? mainPathSplit[1] : mainPathSplit[2]}`,
      ]}
      defaultOpenKeys={!topMenu ? [`${mainPathSplit.length > 2 ? mainPathSplit[1] : 'dashboard'}`] : []}
      overflowedIndicator={<UilEllipsisV />}
      openKeys={openKeys}
      items={getMenuItems()}
    />
  );
}

MenuItems.propTypes = {
  toggleCollapsed: propTypes.func,
};

export default MenuItems;
